import errorReporter from '@app/errorReporter';
import { CustomAgentJsonType } from '@bb/domain/dist/types/CustomAgent';
import { Color, Flex, Text } from '@bb/elements-web';
import type { InputRef } from 'antd';
import { Button, Input, message, Required } from 'components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getPhoneInputMask } from 'utils';
import saveCustomAgent from '../../../../../api/CustomAgentController/save';
import updateCustomAgent from '../../../../../api/CustomAgentController/update';
import CoopSelectContext from './CoopSelectContext';

export type CustomAgentCreateProps = {
  onCustomAgentSubmit: (c: CustomAgentJsonType) => void;
  onSearchRoster: () => void;
};

const CustomAgentCreate: React.FC<CustomAgentCreateProps> = ({
  onCustomAgentSubmit,
  onSearchRoster,
}) => {
  const { coop, coopAdditionalData } = useContext(CoopSelectContext);
  const isEdit = Boolean(
    coop && coopAdditionalData?.coopType === 'customAgent',
  );

  const [customAgent, setCustomAgent] = useState<
    Parameters<typeof saveCustomAgent>[0]
  >(() => {
    if (isEdit) {
      return {
        firstName: coop!.firstName!,
        lastName: coop!.lastName!,
        email: coop!.email,
        phone: coop!.phone,
        companyName: coopAdditionalData!.orgName,
        companyPhone: coopAdditionalData!.orgPhone,
        companyAddress: coopAdditionalData!.orgAddress,
      };
    }
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      companyPhone: '',
      companyAddress: '',
    };
  });
  const [errorMessages, setErrorMessages] = useState<
    Record<Exclude<keyof typeof customAgent, 'companyAddress'>, string>
  >({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    companyPhone: '',
  });
  const phoneRef: React.ForwardedRef<InputRef> = useRef<InputRef>(null);
  const companyPhoneRef: React.ForwardedRef<InputRef> = useRef<InputRef>(null);

  useEffect(() => {
    if (phoneRef.current && companyPhoneRef.current) {
      getPhoneInputMask().mask([
        phoneRef.current.input!,
        companyPhoneRef.current.input!,
      ]);
    }
  }, []);

  const handleSubmit = async () => {
    const { firstName, lastName, phone, email, companyName, companyPhone } =
      customAgent;
    const newErrorMessages: typeof errorMessages = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      companyPhone: '',
    };
    let errorCount = 0;
    if (!firstName) {
      newErrorMessages.firstName = 'First name is required.';
      errorCount += 1;
    }
    if (!lastName) {
      newErrorMessages.lastName = 'Last name required.';
      errorCount += 1;
    }
    if (phone && !phoneRef.current?.input?.inputmask?.isValid()) {
      newErrorMessages.phone = 'Invalid phone number';
      errorCount += 1;
    }
    if (!companyPhone) {
      newErrorMessages.companyPhone = 'Company phone is required.';
      errorCount += 1;
    } else if (!companyPhoneRef.current?.input?.inputmask?.isValid()) {
      newErrorMessages.companyPhone = 'Invalid phone number';
      errorCount += 1;
    }
    if (!email) {
      newErrorMessages.email = 'Email is required.';
      errorCount += 1;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      newErrorMessages.email = 'Invalid email address.';
      errorCount += 1;
    }
    if (!companyName) {
      newErrorMessages.companyName = 'Company name is required.';
      errorCount += 1;
    }
    setErrorMessages(newErrorMessages);
    if (errorCount) {
      message.warning(
        `At least ${errorCount} field${
          errorCount !== 1 ? 's need to be revised' : ' needs to be revised'
        }`,
      );
      return;
    }

    try {
      let createdCustomAgent: Awaited<ReturnType<typeof saveCustomAgent>>;
      if (isEdit) {
        createdCustomAgent = await updateCustomAgent(customAgent, coop!.id!);
      } else {
        createdCustomAgent = await saveCustomAgent(customAgent);
      }
      onCustomAgentSubmit(createdCustomAgent);
    } catch (err) {
      errorReporter.error(err);
      message.error(`Failed to ${isEdit ? 'update' : 'save'} custom agent`);
    }
  };

  const onChange = (
    fieldName: keyof typeof customAgent,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.currentTarget;
    setCustomAgent(prev => ({ ...prev, [fieldName]: value }));
  };

  return (
    <StyledFlex vertical gap="middle">
      <Flex justify="space-between">
        <Text>
          <Required /> = Required field
        </Text>
        <Button
          size="small"
          type="text"
          data-cy="customAgentCreate-searchRoster-btn"
          onClick={onSearchRoster}
        >
          Search Roster
        </Button>
      </Flex>
      <Flex justify="space-between" gap="large">
        <Input
          required
          error={errorMessages.firstName || ''}
          label="First Name"
          onChange={e => onChange('firstName', e)}
          value={customAgent.firstName}
        />
        <Input
          required
          error={errorMessages.lastName || ''}
          label="Last Name"
          onChange={e => onChange('lastName', e)}
          value={customAgent.lastName}
        />
      </Flex>
      <Flex justify="space-between" gap="large">
        <Input
          required
          error={errorMessages.email || ''}
          label="Email"
          onChange={e => onChange('email', e)}
          value={customAgent.email}
        />
        <Input
          error={errorMessages.phone || ''}
          label="Mobile"
          ref={phoneRef}
          placeholder="Phone"
          onChange={e => onChange('phone', e)}
          value={customAgent.phone}
        />
      </Flex>
      <Input
        required
        error={errorMessages.companyName || ''}
        label="Company Name"
        onChange={e => onChange('companyName', e)}
        value={customAgent.companyName}
      />
      <Flex justify="space-between" gap="large">
        <Input
          required
          error={errorMessages.companyPhone || ''}
          ref={companyPhoneRef}
          label="Company Phone"
          onChange={e => onChange('companyPhone', e)}
          value={customAgent.companyPhone}
        />
        <Input
          label="Company Address"
          onChange={e => onChange('companyAddress', e)}
          value={customAgent.companyAddress}
        />
      </Flex>
      <DangerText thickness="bold">
        This person is NOT a verified agent
      </DangerText>
      <Flex justify="flex-end" gap="middle">
        <Button
          type="primary"
          data-cy="customAgentCreate-save-btn"
          onClick={handleSubmit}
        >
          {isEdit ? 'Update' : 'Save'} Profile
        </Button>
        <Button
          type="default"
          data-cy="customAgentCreate-cancel-btn"
          onClick={onSearchRoster}
        >
          Cancel
        </Button>
      </Flex>
    </StyledFlex>
  );
};

export const StyledFlex = styled(Flex)`
  margin-top: 16px;
`;

export const DangerText = styled(Text)`
  color: ${Color.JANEMBA};
`;

export default CustomAgentCreate;
